/**
 * @file Maintenance parent-form fields and create/edit-flow ownership.
 */

import { openParentForm } from "../../shared/forms/parentFormModal.js";
import { appData as configuredAppData } from "../../app/state/state.js";
import {
  FIELD_WRAPPER_CLASSES,
  NATIVE_INPUT_CLASSES,
  FIXED_TEXTAREA_CLASSES,
  REQUIRED_MARK_HTML,
  bindNativeFieldBehavior,
  getFieldLabelHtml,
} from "../../shared/forms/nativeFieldStyles.js";
import { registerParentFormAdapter } from "../../shared/forms/parentFormRegistry.js";
import { setFormItemData } from "../../shared/forms/formModalState.js";
import {
  closeForm,
  configureDefaultFormActions,
  formModalElement,
} from "../../shared/forms/modalShell.js";
import { openConfirmationModal } from "../../shared/ui/confirmationModal.js";
import { destroyRegisteredControlsIn } from "../../shared/ui/controls/controlCore.js";
import {
  getDynamicFieldHtml,
  mountDynamicSelectControls,
  renderDynamicFieldForm,
} from "../../shared/forms/dynamicFields.js";
import { formatDate, getLocalDateString } from "../../shared/utils/dateUtils.js";
import { formatCurrency } from "../../shared/utils/displayUtils.js";
import {
  resolveItemClosedFromStatus,
} from "../../shared/utils/closedState.js";
import { getAssignableUsersWithPermission } from "../../shared/utils/userDisplay.js";
import {
  saveMaintenanceItem,
  updateMaintenanceAssetLink,
} from "./maintenanceApi.js";
import {
  getMaintenanceCloseFailureMessage,
  openMaintenanceCloseModal,
} from "./maintenanceCloseModal.js";
import {
  attachMaintenanceAssetLinkFieldListeners,
  getMaintenanceAssetDisplay,
  openMaintenanceAssetLookupModal,
  renderMaintenanceAssetLinkField,
} from "./maintenanceAssetLookupModal.js";
import {
  mountMaintenanceTechnicianControl,
  renderMaintenanceTechnicianControlRoot,
} from "./maintenanceTechnicianControl.js";

const MAINTENANCE_STREAMS = Object.freeze([
  {
    value: "issue",
    label: "Issue",
    icon: "report",
    color: "#d65f5f",
    fillColor: "#fde3e3",
    body: "Log a fault or equipment problem that needs follow-up.",
  },
  {
    value: "service",
    label: "Service",
    icon: "build_circle",
    color: "#4599e8",
    fillColor: "#e4f2ff",
    body: "Plan scheduled, routine, or requested service work.",
  },
]);
const SERVICE_TITLE_MONTHS = Object.freeze([
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
]);

function attrValue(value = "") {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function textValue(value = "") {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function fieldHtml(label, controlHtml, options = {}) {
  const wrapperClass = [FIELD_WRAPPER_CLASSES, options.className || ""]
    .filter(Boolean)
    .join(" ");
  return `
    <div class="${wrapperClass}">
      ${getFieldLabelHtml(label, Boolean(options.required))}
      ${controlHtml}
    </div>
  `;
}

function setFieldLabelRequired(control, labelText, required) {
  const label = control?.parentElement?.querySelector?.(":scope > label");
  if (!label) {return;}
  label.innerHTML = `${textValue(labelText)}${required ? REQUIRED_MARK_HTML : ""}`;
}

function inputHtml({
  type = "text",
  name,
  value = "",
  placeholder = "",
  required = false,
  className = NATIVE_INPUT_CLASSES,
  extraAttrs = "",
}) {
  const placeholderAttr = placeholder ? ` placeholder="${attrValue(placeholder)}"` : "";
  return `<input type="${type}" name="${name}" value="${attrValue(value)}" class="${className}"${placeholderAttr}${required ? " required" : ""}${extraAttrs ? ` ${extraAttrs}` : ""}>`;
}

function textareaHtml({
  name,
  value = "",
  placeholder = "",
  required = false,
}) {
  const placeholderAttr = placeholder ? ` placeholder="${attrValue(placeholder)}"` : "";
  return `<textarea name="${name}" class="${FIXED_TEXTAREA_CLASSES}"${placeholderAttr}${required ? " required" : ""}>${textValue(value)}</textarea>`;
}

function getOptionSupportsStream(option, stream) {
  const streams = Array.isArray(option?.streams) ?
    option.streams.map((entry) => String(entry)).filter(Boolean) :
    [];
  return streams.length === 0 || streams.includes(stream);
}

function getDefaultStreamOptionId(options = [], stream = "") {
  const activeOptions = (Array.isArray(options) ? options : [])
    .filter((option) => option?.isActive !== false)
    .filter((option) => getOptionSupportsStream(option, stream));
  return activeOptions.find((option) => option.default)?.id ||
    activeOptions[0]?.id ||
    "";
}

function getMaintenanceStream(itemData = {}) {
  const stream = String(itemData.stream || "");
  return stream === "issue" ? "issue" : "service";
}

function getServiceTitleDateParts(dateString = getLocalDateString()) {
  const match = String(dateString || "").match(/^(\d{4})-(\d{2})-\d{2}$/);
  if (!match) {
    const fallback = new Date();
    return {
      month: SERVICE_TITLE_MONTHS[fallback.getMonth()] || "Jan",
      year: fallback.getFullYear(),
    };
  }
  const monthIndex = Number(match[2]) - 1;
  return {
    month: SERVICE_TITLE_MONTHS[monthIndex] || "Jan",
    year: match[1],
  };
}

function getGeneratedServiceTitle(assetName, dateString = getLocalDateString()) {
  const name = String(assetName || "").trim();
  if (!name) {return "";}
  const {month, year} = getServiceTitleDateParts(dateString);
  return `${month} ${year} Service: ${name}`;
}

function isGeneratedServiceTitleForAsset(title, assetName) {
  const name = String(assetName || "").trim();
  const value = String(title || "").trim();
  if (!name || !value) {return false;}
  return new RegExp(
    `^[A-Z][a-z]{2} \\d{4} Service: ${name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}$`,
  ).test(value);
}

function shouldSyncServiceTitle({currentItem, previousAssetName}) {
  const item = String(currentItem || "").trim();
  const previousName = String(previousAssetName || "").trim();
  return !item ||
    item === previousName ||
    isGeneratedServiceTitleForAsset(item, previousName);
}

function getPreparedMaintenanceDefaults(defaults = {}) {
  const prepared = {...defaults};
  const stream = getMaintenanceStream(prepared);
  const linkedAssetName = String(
    prepared.linked_asset_name ||
      prepared.assetName ||
      prepared.name ||
      "",
  ).trim();
  const item = String(prepared.item || "").trim();
  if (
    stream === "service" &&
    prepared.linked_asset_id &&
    linkedAssetName &&
    !item
  ) {
    prepared.item = getGeneratedServiceTitle(linkedAssetName);
  }
  return prepared;
}

function getMaintenanceStreamMeta(stream) {
  return MAINTENANCE_STREAMS.find((entry) => entry.value === stream) ||
    MAINTENANCE_STREAMS[1];
}

function getDefaultManagedBy({appData, formItemData, isEditing, state}) {
  if (isEditing && formItemData.managed_by) {return formItemData.managed_by;}

  const configuredUserId = appData?.config?.maintenance?.defaultManagedBy || "";
  const assignableUsers = getAssignableUsersWithPermission("maintenance");
  const configuredUser = assignableUsers.find((user) => {
    return user.id === configuredUserId && user.isActive !== false;
  });
  return configuredUser?.id ||
    formItemData.managed_by ||
    state.currentUser?.uid ||
    "";
}

function getCloseInterceptStatusId({appData, stream}) {
  return appData?.config?.maintenance?.closeInterceptStatusByStream?.[stream] || "";
}

function isCloseInterceptStatus({appData, status, stream}) {
  const closeStatusId = getCloseInterceptStatusId({appData, stream});
  return Boolean(closeStatusId && status === closeStatusId);
}

function renderServiceFields(val, options = {}) {
  const showCloseFields = Boolean(options.showCloseFields);
  return `
    ${fieldHtml("Due Date", '<div data-maintenance-date-control="due_date"></div>')}
    <div
      class="${showCloseFields ? "contents" : "hidden"}"
      data-maintenance-service-close-fields
    >
      ${fieldHtml("Completed On", '<div data-maintenance-date-control="completed_on"></div>', {required: showCloseFields})}
      ${fieldHtml("Cost", inputHtml({
        name: "cost",
        value: formatCurrency(val("cost")),
        placeholder: "$",
        required: showCloseFields,
        className: `${NATIVE_INPUT_CLASSES} currency-input`,
        extraAttrs: 'inputmode="decimal"',
      }), {required: showCloseFields})}
    </div>
    ${fieldHtml("Details", textareaHtml({
      name: "details",
      value: val("details"),
    }), {className: "md:col-span-2"})}
  `;
}

function getSectionHeadingPattern(sectionName) {
  return new RegExp(`^\\*\\*${sectionName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\*\\*$`, "i");
}

function parseGeneratedDetailsSections(details = "") {
  const lines = String(details || "").split(/\r?\n/);
  const sections = new Map();
  let currentSection = null;
  let currentLines = [];
  const flushSection = () => {
    if (!currentSection) {return;}
    sections.set(currentSection.toLowerCase(), currentLines.join("\n").trim());
  };

  lines.forEach((line) => {
    const headingMatch = line.trim().match(/^\*\*(.+)\*\*$/);
    if (headingMatch) {
      flushSection();
      currentSection = headingMatch[1].trim();
      currentLines = [];
      return;
    }

    if (currentSection) {
      currentLines.push(line);
    }
  });
  flushSection();
  return sections;
}

function getGeneratedDetailsSection(details, sectionName) {
  return parseGeneratedDetailsSections(details).get(sectionName.toLowerCase()) || "";
}

function stripGeneratedDetailsSections(details = "", sectionNames = []) {
  const sectionPatterns = sectionNames.map(getSectionHeadingPattern);
  const lines = String(details || "").split(/\r?\n/);
  const keptLines = [];
  let skipping = false;

  lines.forEach((line) => {
    const trimmedLine = line.trim();
    const isAnyHeading = /^\*\*.+\*\*$/.test(trimmedLine);
    const isTargetHeading = sectionPatterns.some((pattern) =>
      pattern.test(trimmedLine),
    );

    if (isTargetHeading) {
      skipping = true;
      return;
    }

    if (isAnyHeading) {
      skipping = false;
    }

    if (!skipping) {
      keptLines.push(line);
    }
  });

  return keptLines.join("\n").trim();
}

function renderDetailsSection(title, body) {
  const content = String(body || "").trim();
  if (!content) {return "";}
  return `**${title}**\n${content}`;
}

function getMaintenanceIssueDetailParts(itemData = {}) {
  const details = String(itemData.details || "");
  const issueDetails = getGeneratedDetailsSection(details, "Issue Details");
  const generalDetails = getGeneratedDetailsSection(details, "General Details");
  const generatedStripped = stripGeneratedDetailsSections(details, [
    "Issue Details",
    "General Details",
  ]);

  return {
    issueDetails: issueDetails || (generalDetails ? "" : generatedStripped),
    generalDetails,
  };
}

/**
 * Composes the canonical Maintenance details text.
 * @param {object} params Details parts.
 * @param {string} [params.dynamicDetails] Dynamic field markdown.
 * @param {string} [params.issueDetails] Issue details text.
 * @param {string} [params.generalDetails] Optional general details text.
 * @returns {string} Canonical details text.
 */
export function composeMaintenanceDetailsForSubmit({
  dynamicDetails = "",
  issueDetails = "",
  generalDetails = "",
} = {}) {
  return [
    String(dynamicDetails || "").trim(),
    renderDetailsSection("Issue Details", issueDetails),
    renderDetailsSection("General Details", generalDetails),
  ].filter(Boolean).join("\n\n");
}

function renderIssueFields() {
  return `
    ${fieldHtml("Identified On", '<div data-maintenance-date-control="identified_on"></div>')}
    ${fieldHtml("Identified By", '<div data-maintenance-user-control="identified_by"></div>')}
  `;
}

function renderIssueDetailsField(val) {
  const issueParts = getMaintenanceIssueDetailParts({
    details: val("details"),
  });
  return `
    ${fieldHtml("Issue Details", textareaHtml({
      name: "issue_details",
      value: issueParts.issueDetails,
      required: true,
    }), {required: true, className: "md:col-span-2"})}
  `;
}

function renderIssueCloseFields(val, options = {}) {
  const showCloseFields = Boolean(options.showCloseFields);
  return `
    <div
      class="${showCloseFields ? "contents" : "hidden"}"
      data-maintenance-issue-close-fields
    >
      ${fieldHtml("Technician", `
        ${renderMaintenanceTechnicianControlRoot()}
      `, {required: true})}
      ${fieldHtml("Completed On", '<div data-maintenance-date-control="completed_on"></div>', {required: true})}
      ${fieldHtml("Cost", inputHtml({
        name: "cost",
        value: formatCurrency(val("cost")),
        placeholder: "$",
        required: true,
        className: `${NATIVE_INPUT_CLASSES} currency-input`,
        extraAttrs: 'inputmode="decimal"',
      }), {required: true})}
      <div data-maintenance-repair-fields-anchor class="contents"></div>
      ${fieldHtml("Repair Details", textareaHtml({
        name: "repair_details",
        value: val("repair_details"),
      }), {className: "md:col-span-2"})}
    </div>
  `;
}

function renderStreamPickerFields() {
  return `
    <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
      ${MAINTENANCE_STREAMS.map((stream) => `
        <button
          type="button"
          class="maintenance-stream-choice group flex min-h-32 flex-col items-center justify-center rounded-lg border border-gray-200 bg-white p-4 text-center shadow-sm transition-colors hover:border-[var(--dfc-brand-primary)] hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--dfc-brand-primary-ring)] sm:min-h-36"
          data-maintenance-stream="${stream.value}"
        >
          <span class="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-black transition-colors group-hover:bg-[var(--dfc-brand-primary)] group-hover:text-[var(--dfc-brand-primary-contrast)]">
            <span class="material-symbols-outlined text-[25px] leading-none">${stream.icon}</span>
          </span>
          <span class="text-base font-semibold text-gray-900">${stream.label}</span>
          <span class="mt-1 max-w-44 text-xs leading-5 text-gray-600">${stream.body}</span>
        </button>
      `).join("")}
    </div>
  `;
}

function syncMaintenanceDynamicFields(typeOptions, selectedTypeIds, itemForm) {
  const selectedSet = new Set(selectedTypeIds);
  itemForm.querySelectorAll('[id^="dynamic-fields-"]:not(#dynamic-fields-anchor)')
    .forEach((container) => {
      const typeId = container.id.replace("dynamic-fields-", "");
      if (!selectedSet.has(typeId)) {
        destroyRegisteredControlsIn(container);
        container.remove();
      }
    });

  selectedTypeIds.forEach((typeId) => {
    const typeConfig = typeOptions.find((option) => option.id === typeId);
    if (typeConfig) {
      renderDynamicFieldForm(typeConfig, itemForm);
    }
  });
}

function getRepairDynamicFieldName(fieldConfig) {
  const label = fieldConfig?.label || "Field";
  const baseName = fieldConfig?.name || label.replace(/\s+/g, "_").toLowerCase();
  return `repair_${baseName}`;
}

function getRepairDynamicRenderFields(fields = []) {
  return fields.map((field) => ({
    ...field,
    name: getRepairDynamicFieldName(field),
  }));
}

function getMaintenanceTypeOptions({appData, selectedTypes = [], stream}) {
  return (appData?.config?.maintenanceTypes || [])
    .filter((option) => {
      return selectedTypes.includes(option?.id) ||
        option?.isActive !== false && getOptionSupportsStream(option, stream);
    });
}

function getSelectedRepairTypeConfigs({appData, formItemData}) {
  const selectedTypes = Array.isArray(formItemData?.types) ?
    formItemData.types :
    [];
  const stream = getMaintenanceStream(formItemData);
  return getMaintenanceTypeOptions({appData, selectedTypes, stream})
    .filter((typeConfig) => {
      return selectedTypes.includes(typeConfig?.id) &&
        Array.isArray(typeConfig?.repairDynamicFields) &&
        typeConfig.repairDynamicFields.length > 0;
    });
}

function renderIssueRepairDynamicFields({
  appData,
  formFields,
  formItemData,
  show,
}) {
  const anchor = formFields.querySelector("[data-maintenance-repair-fields-anchor]");
  if (!anchor) {return;}
  anchor.replaceChildren();
  if (!show) {return;}

  getSelectedRepairTypeConfigs({appData, formItemData}).forEach((typeConfig) => {
    const repairFields = getRepairDynamicRenderFields(typeConfig.repairDynamicFields);
    const container = document.createElement("div");
    container.id = `maintenance-repair-fields-${typeConfig.id}`;
    container.className = "md:col-span-2 rounded-lg bg-blue-100/40 p-4 dark:bg-blue-900/20";
    container.dataset.maintenanceRepairFieldGroup = typeConfig.id || "";
    container.innerHTML = `
      <h5 class="mb-3 text-sm font-semibold text-blue-800 dark:text-blue-300">${textValue(typeConfig.name || "Repair")} Repair Fields</h5>
      <div class="grid grid-cols-1 gap-x-4 gap-y-3 md:grid-cols-4">
        ${repairFields.map(getDynamicFieldHtml).join("")}
      </div>
    `;
    anchor.append(container);
    bindNativeFieldBehavior(container);
    mountDynamicSelectControls(container, {
      fields: repairFields,
    });
  });
}

function getRepairFieldMarkdown(form, appData, formItemData) {
  const lines = [];
  getSelectedRepairTypeConfigs({appData, formItemData}).forEach((typeConfig) => {
    typeConfig.repairDynamicFields.forEach((field) => {
      if (field?.type === "heading") {return;}
      const name = getRepairDynamicFieldName(field);
      const input = form.querySelector(`[name="${CSS.escape(name)}"]`);
      if (!input) {return;}

      let value = input.type === "checkbox" ?
        (input.checked ? "Yes" : "") :
        input.value;
      if ((input.type === "date" || input.type === "datetime-local") && value) {
        value = formatDate(value);
      }
      if (!String(value || "").trim()) {return;}

      const label = String(field?.label || name).trim();
      const valueLines = String(value).split("\n");
      lines.push(`**${label}**: ${valueLines[0]}`);
      valueLines.slice(1).forEach((line) => {
        lines.push(line);
      });
    });
  });
  return lines.join("\n").trim();
}

function removeRepairDynamicFieldKeys(data, appData, formItemData) {
  getSelectedRepairTypeConfigs({appData, formItemData}).forEach((typeConfig) => {
    typeConfig.repairDynamicFields.forEach((field) => {
      if (field?.type !== "heading") {
        delete data[getRepairDynamicFieldName(field)];
      }
    });
  });
}

function mountTechnicianInput({
  appData,
  disabled = false,
  formFields,
  formItemData,
  required = false,
}) {
  return mountMaintenanceTechnicianControl({
    root: formFields.querySelector("[data-maintenance-technician-control]"),
    appData,
    value: formItemData.technician || "",
    required,
    disabled,
    onChange: (value) => {
      formItemData.technician = value || "";
    },
  });
}

function syncServiceCloseFields({appData, controls = {}, formFields, formItemData}) {
  const root = formFields.querySelector("[data-maintenance-service-close-fields]");
  if (!root) {return;}
  const stream = getMaintenanceStream(formItemData);
  const show = stream === "service" && isCloseInterceptStatus({
    appData,
    status: formItemData.status || "",
    stream,
  });
  root.classList.toggle("contents", show);
  root.classList.toggle("hidden", !show);
  root.querySelectorAll("input, select, textarea").forEach((field) => {
    field.disabled = !show;
    field.required = show;
  });
  setFieldLabelRequired(
    formFields.querySelector("[data-maintenance-technician-control]"),
    "Technician",
    show,
  );
  setFieldLabelRequired(
    root.querySelector('[data-maintenance-date-control="completed_on"]'),
    "Completed On",
    show,
  );
  setFieldLabelRequired(root.querySelector('input[name="cost"]'), "Cost", show);
  controls.technician?.update?.({
    disabled: false,
    required: show,
  });
  controls.completed_on?.update?.({
    disabled: !show,
    required: show,
  });
}

function syncIssueCloseFields({
  appData,
  controls = {},
  formFields,
  formItemData,
}) {
  const root = formFields.querySelector("[data-maintenance-issue-close-fields]");
  if (!root) {return;}
  const stream = getMaintenanceStream(formItemData);
  const show = stream === "issue" && isCloseInterceptStatus({
    appData,
    status: formItemData.status || "",
    stream,
  });
  root.classList.toggle("contents", show);
  root.classList.toggle("hidden", !show);
  root.querySelectorAll("input, select, textarea").forEach((field) => {
    field.disabled = !show;
  });
  controls.technician?.update?.({
    disabled: !show,
    required: show,
  });
  controls.completed_on?.update?.({
    disabled: !show,
    required: show,
  });
  renderIssueRepairDynamicFields({
    appData,
    formFields,
    formItemData,
    show,
  });
}

function syncMaintenanceCloseFields(context) {
  syncServiceCloseFields(context);
  syncIssueCloseFields(context);
}

function syncItemInputFromAssetLink({formFields, formItemData}) {
  const itemInput = formFields.querySelector('input[name="item"]');
  if (!itemInput) {return;}

  const hasAssetLink = Boolean(formItemData.linked_asset_id);
  if (hasAssetLink && formItemData.item) {
    itemInput.value = formItemData.item;
  }
}

function setSelectedAsset({asset, formFields, formItemData}) {
  const itemInput = formFields.querySelector('input[name="item"]');
  const currentItem = String(itemInput?.value || formItemData.item || "").trim();
  const previousAssetName = String(formItemData.linked_asset_name || "").trim();
  const stream = getMaintenanceStream(formItemData);
  const shouldSyncItem = stream === "service" &&
    shouldSyncServiceTitle({currentItem, previousAssetName});

  formItemData.linked_asset_id = asset?.id || "";
  formItemData.linked_asset_name = asset?.name || "";
  formItemData.linked_asset_serial_number = asset?.serial_number || "";
  formItemData.linked_asset_types = Array.isArray(asset?.types) ? asset.types : [];
  if (asset?.name && shouldSyncItem) {
    formItemData.item = getGeneratedServiceTitle(asset.name);
  } else if (itemInput) {
    formItemData.item = itemInput.value || formItemData.item || "";
  }
  renderAssetLinkFieldInForm({formFields, formItemData});
  syncItemInputFromAssetLink({formFields, formItemData});
}

function clearSelectedAsset({formFields, formItemData}) {
  formItemData.linked_asset_id = "";
  formItemData.linked_asset_name = "";
  formItemData.linked_asset_serial_number = "";
  formItemData.linked_asset_types = [];
  const itemInput = formFields.querySelector('input[name="item"]');
  if (itemInput) {
    formItemData.item = itemInput.value || formItemData.item || "";
  }
  renderAssetLinkFieldInForm({formFields, formItemData});
  syncItemInputFromAssetLink({formFields, formItemData});
}

function renderAssetLinkFieldInForm({formFields, formItemData}) {
  const field = formFields.querySelector(
    '[data-linked-parent-field="maintenance-linked-asset"]',
  );
  if (!field) {return;}

  field.outerHTML = renderMaintenanceAssetLinkField({
    source: formItemData,
    canLink: true,
    canUnlink: true,
    canOpen: false,
  });
  attachMaintenanceCreateAssetLinkListeners({formFields, formItemData});
}

function attachMaintenanceCreateAssetLinkListeners({formFields, formItemData}) {
  attachMaintenanceAssetLinkFieldListeners(formFields, {
    onLink: () => {
      openMaintenanceAssetLookupModal({
        selectedAssetId: formItemData.linked_asset_id || "",
        selectedAsset: getMaintenanceAssetDisplay(formItemData),
        onSelect: async (asset) => {
          setSelectedAsset({asset, formFields, formItemData});
          return true;
        },
      });
    },
    onUnlink: () => {
      clearSelectedAsset({formFields, formItemData});
    },
  });
}

function mountMaintenanceScalarControls(context) {
  const {
    appData,
    createDateControl,
    createPrioritySelectPreset,
    createStreamScopedStatusSelectPreset,
    formFields,
    formItemData,
    isEditing,
    itemForm,
    state,
  } = context;
  const stream = getMaintenanceStream(formItemData);
  const controls = {};

  if (!Object.prototype.hasOwnProperty.call(formItemData, "__original_linked_asset_id")) {
    Object.defineProperty(formItemData, "__original_linked_asset_id", {
      configurable: true,
      enumerable: false,
      value: isEditing ? formItemData.linked_asset_id || "" : "",
      writable: true,
    });
  }

  attachMaintenanceCreateAssetLinkListeners({formFields, formItemData});
  syncItemInputFromAssetLink({formFields, formItemData});
  if (formFields.querySelector("[data-maintenance-technician-control]")) {
    const issueCloseRoot = formFields.querySelector("[data-maintenance-issue-close-fields]");
    const isIssueCloseTechnician = Boolean(issueCloseRoot);
    const showIssueCloseFields = isIssueCloseTechnician && isCloseInterceptStatus({
      appData,
      status: formItemData.status || "",
      stream,
    });
    controls.technician = mountTechnicianInput({
      appData,
      disabled: isIssueCloseTechnician && !showIssueCloseFields,
      formFields,
      formItemData,
      required: isIssueCloseTechnician,
    });
  }

  const managedByRoot = formFields.querySelector(
    '[data-maintenance-user-control="managed_by"]',
  );
  if (managedByRoot) {
    controls.managed_by = context.createUserSelectPreset({
      root: managedByRoot,
      appData,
      name: "managed_by",
      collectionName: "maintenance",
      value: getDefaultManagedBy({appData, formItemData, isEditing, state}),
      clearable: false,
      searchable: true,
      onChange: (value) => {
        formItemData.managed_by = value || "";
      },
    });
  }

  const identifiedByRoot = formFields.querySelector(
    '[data-maintenance-user-control="identified_by"]',
  );
  if (identifiedByRoot) {
    controls.identified_by = context.createUserSelectPreset({
      root: identifiedByRoot,
      appData,
      name: "identified_by",
      collectionName: "maintenance",
      value: formItemData.identified_by || state.currentUser?.uid || "",
      clearable: false,
      searchable: true,
      onChange: (value) => {
        formItemData.identified_by = value || "";
      },
    });
  }

  const priorityRoot = formFields.querySelector(
    '[data-maintenance-scalar-control="priority"]',
  );
  if (priorityRoot) {
    controls.priority = createPrioritySelectPreset({
      root: priorityRoot,
      appData,
      value: formItemData.priority ||
        getDefaultStreamOptionId(appData?.config?.priorities || [], stream),
      clearable: false,
    });
  }

  const statusRoot = formFields.querySelector(
    '[data-maintenance-scalar-control="status"]',
  );
  if (statusRoot) {
    controls.status = createStreamScopedStatusSelectPreset({
      root: statusRoot,
      appData,
      itemType: "maintenance",
      stream,
      value: formItemData.status ||
        getDefaultStreamOptionId(appData?.config?.maintenanceStatus || [], stream),
      clearable: false,
      onChange: (value) => {
        formItemData.status = value || "";
        syncMaintenanceCloseFields({appData, controls, formFields, formItemData});
      },
    });
    formItemData.status = controls.status.value || formItemData.status || "";
    syncMaintenanceCloseFields({appData, controls, formFields, formItemData});
  }

  formFields.querySelectorAll("[data-maintenance-date-control]")
    .forEach((root) => {
      const name = root.dataset.maintenanceDateControl;
      const issueCloseRoot = root.closest("[data-maintenance-issue-close-fields]");
      const closeRoot = issueCloseRoot ||
        root.closest("[data-maintenance-service-close-fields]");
      controls[name] = createDateControl({
        root,
        name,
        type: "date",
        required: Boolean(issueCloseRoot),
        disabled: Boolean(closeRoot?.classList.contains("hidden")),
        value: formItemData[name] ||
          (name === "identified_on" ? getLocalDateString() : ""),
      });
    });

  syncMaintenanceCloseFields({appData, controls, formFields, formItemData});

  controls.types = mountMaintenanceTypeControl({
    ...context,
    itemForm,
    stream,
  });

  return controls;
}

function mountMaintenanceTypeControl({
  appData,
  createStreamScopedTypeSelectPreset,
  formFields,
  formItemData,
  itemForm,
  stream,
}) {
  const root = formFields.querySelector('[data-maintenance-type-control]');
  if (!root) {return null;}

  const selectedTypes = Array.isArray(formItemData.types) ?
    formItemData.types :
    [];
  const typeOptions = getMaintenanceTypeOptions({
    appData,
    selectedTypes,
    stream,
  });
  formItemData.types = selectedTypes;

  const control = createStreamScopedTypeSelectPreset({
    root,
    appData,
    itemType: "maintenance",
    stream,
    options: typeOptions,
    value: selectedTypes,
    searchable: true,
    onChange: (value) => {
      const nextTypes = Array.isArray(value) ? value : [];
      formItemData.types = nextTypes;
      syncMaintenanceDynamicFields(typeOptions, nextTypes, itemForm);
      syncIssueCloseFields({appData, formFields, formItemData});
    },
  });
  syncMaintenanceDynamicFields(typeOptions, selectedTypes, itemForm);
  syncIssueCloseFields({appData, formFields, formItemData});
  return control;
}

function prepareIssueDetails({data, form, formItemData}) {
  if (data.stream !== "issue") {
    delete data.issue_details;
    delete data.maintenance_general_details;
    return true;
  }

  const issueDetails = String(data.issue_details || "").trim();
  if (!issueDetails) {
    openConfirmationModal(
      "Issue Details Required",
      "Enter the issue details before saving this Maintenance item.",
      async () => {
        const input = form.querySelector('[name="issue_details"]');
        input?.focus();
      },
      false,
      "OK",
    );
    return false;
  }

  const dynamicDetails = String(data.details || "").trim();
  data.details = composeMaintenanceDetailsForSubmit({
    dynamicDetails,
    issueDetails,
  });
  const repairFieldMarkdown = getRepairFieldMarkdown(
    form,
    configuredAppData,
    formItemData,
  );
  data.repair_details = [
    String(data.repair_details || "").trim(),
    repairFieldMarkdown,
  ].filter(Boolean).join("\n\n");
  removeRepairDynamicFieldKeys(data, configuredAppData, formItemData);
  delete data.issue_details;
  delete data.maintenance_general_details;
  return true;
}

function applyMaintenanceSubmitData({data, formItemData}) {
  data.stream = getMaintenanceStream(formItemData);
  data.types = Array.isArray(data.types) ? data.types : formItemData.types || [];
  data.linked_asset_id = data.linked_asset_id || null;
  data.technician = String(data.technician || "").trim();
  data.cost = data.cost || null;
  const isClosedIssueCreate = data.stream === "issue" && isCloseInterceptStatus({
    appData: configuredAppData,
    status: data.status || formItemData?.status || "",
    stream: data.stream,
  });
  if (data.stream === "issue" && !isClosedIssueCreate) {
    delete data.technician;
    delete data.cost;
    delete data.completed_on;
    delete data.repair_details;
  }
}

function isMaintenanceCompletionRequiredResponse(result) {
  return result?.success === false &&
    result?.reason === "maintenance_completion_required";
}

function getMaintenanceSaveFailureMessage(result) {
  return getMaintenanceCloseFailureMessage(result) ||
    "Maintenance could not be saved.";
}

async function persistMaintenanceFormPayload({
  assetId,
  hasAssetLinkChange,
  id,
  payload,
}) {
  const result = await saveMaintenanceItem({
    itemId: id || null,
    data: payload,
  });

  if (result?.success === false) {
    return result;
  }

  const maintenanceId = id || result?.itemId || null;
  let linkResult = null;
  if (id && maintenanceId && hasAssetLinkChange) {
    linkResult = await updateMaintenanceAssetLink({
      maintenanceId,
      assetId,
    });
    if (linkResult?.success === false) {
      const blockerMessage = (linkResult.blockers || [])
        .map((blocker) => blocker?.message)
        .filter(Boolean)
        .join(" ");
      throw new Error(
        blockerMessage || "The Maintenance item saved, but the Equipment link was not updated.",
      );
    }
  }

  return linkResult ? {...result, linkResult} : result;
}

function upsertMaintenanceCacheItem(item) {
  if (!item?.id) {return;}
  const items = Array.isArray(configuredAppData.maintenance) ?
    configuredAppData.maintenance :
    [];
  const existingIndex = items.findIndex((entry) => entry?.id === item.id);
  if (existingIndex >= 0) {
    configuredAppData.maintenance = items.map((entry, index) =>
      index === existingIndex ? {...entry, ...item} : entry,
    );
    return;
  }
  configuredAppData.maintenance = [item, ...items];
}

function mergeCreatedMaintenanceIntoCache({
  data = {},
  id,
  saveResult = {},
}) {
  if (id || !saveResult?.itemId) {return;}
  const linkCurrent = saveResult?.initialLinkResult?.current ||
    saveResult?.linkResult?.current ||
    {};
  const linkedAssetId = data.linked_asset_id || linkCurrent.assetId || null;
  const linkedAssetName = data.linked_asset_name || linkCurrent.assetName || "";
  const linkedAssetSerialNumber =
    data.linked_asset_serial_number || linkCurrent.serial_number || "";
  const linkedAssetTypes = Array.isArray(data.linked_asset_types) ?
    data.linked_asset_types :
    Array.isArray(linkCurrent.types) ?
      linkCurrent.types :
      [];

  upsertMaintenanceCacheItem({
    ...data,
    id: saveResult.itemId,
    item: linkCurrent.item || data.item || "",
    linked_asset_id: linkedAssetId,
    linked_asset_name: linkedAssetName,
    linked_asset_serial_number: linkedAssetSerialNumber,
    linked_asset_types: linkedAssetTypes,
    isClosedResolved: resolveItemClosedFromStatus("maintenance", data.status),
    updatedAt: new Date().toISOString(),
  });
}

function buildCompletionModalItem({formItemData, payload}) {
  return {
    ...formItemData,
    ...payload,
    stream: payload.stream || getMaintenanceStream(formItemData),
    status: payload.status || formItemData?.status || "",
    types: Array.isArray(payload.types) ? payload.types : formItemData?.types || [],
  };
}

async function requestCreateCompletionAndRetry({
  appData,
  assetId,
  formItemData,
  hasAssetLinkChange,
  id,
  payload,
}) {
  return new Promise((resolve) => {
    openMaintenanceCloseModal({
      item: buildCompletionModalItem({formItemData, payload}),
      config: appData?.config || {},
      onCancel: () => resolve(false),
      onSubmit: async (completionData) => {
        const retryResult = await persistMaintenanceFormPayload({
          assetId,
          hasAssetLinkChange,
          id,
          payload: {
            ...payload,
            ...completionData,
          },
        });
        if (retryResult?.success === false) {
          throw new Error(getMaintenanceSaveFailureMessage(retryResult));
        }
        resolve(retryResult);
        return true;
      },
    });
  });
}

async function saveMaintenanceFormItem({appData, data, formItemData, id}) {
  const activeAppData = appData || configuredAppData;
  const assetId = Object.prototype.hasOwnProperty.call(data, "linked_asset_id") ?
    data.linked_asset_id || null :
    null;
  const originalAssetId = formItemData?.__original_linked_asset_id || "";
  const hasAssetLinkChange = Object.prototype.hasOwnProperty.call(data, "linked_asset_id") &&
    String(assetId || "") !== String(originalAssetId || "");
  const payload = {...data};
  if (id) {
    delete payload.linked_asset_id;
  }
  delete payload.linked_asset_name;
  delete payload.linked_asset_serial_number;
  delete payload.linked_asset_types;

  const result = await persistMaintenanceFormPayload({
    assetId,
    hasAssetLinkChange,
    id,
    payload,
  });

  if (isMaintenanceCompletionRequiredResponse(result) && !id) {
    return requestCreateCompletionAndRetry({
      appData: activeAppData,
      assetId,
      formItemData,
      hasAssetLinkChange,
      id,
      payload,
    });
  }

  if (result?.success === false) {
    throw new Error(getMaintenanceSaveFailureMessage(result));
  }

  return result;
}

/**
 * Opens the Maintenance stream picker before the standard create form.
 * @param {object} [defaults={}] Optional create defaults.
 * @returns {void}
 */
export function openMaintenanceCreateFlow(defaults = {}) {
  if (defaults?.stream) {
    openParentForm("maintenance", getPreparedMaintenanceDefaults(defaults));
    return;
  }

  if (!formModalElement) {return;}

  const formTitle = formModalElement.querySelector("#form-title");
  const formHeaderActions = formModalElement.querySelector("#form-header-actions");
  const formFields = formModalElement.querySelector("#form-fields");
  const itemForm = formModalElement.querySelector("#item-form");
  const cancelBtn = document.getElementById("cancel-form-btn");
  const submitBtn = formModalElement.querySelector('button[type="submit"]');
  const footer = cancelBtn?.parentElement || submitBtn?.parentElement || null;

  if (!formTitle || !formHeaderActions || !formFields || !itemForm) {return;}

  configureDefaultFormActions();
  setFormItemData({});
  formTitle.textContent = "New Maintenance";
  formHeaderActions.innerHTML = `
    <button
      type="button"
      class="standard-action-icon-btn standard-action-icon-btn--danger shrink-0"
      aria-label="Close Maintenance stream picker"
      data-maintenance-stream-picker-close
    >
      <span class="material-symbols-outlined text-[20px] leading-none">close</span>
    </button>
  `;
  formFields.innerHTML = renderStreamPickerFields();
  itemForm.dataset.type = "maintenance-stream";
  itemForm.dataset.id = "";
  itemForm.dataset.parentId = "";
  itemForm.onsubmit = (event) => event.preventDefault();
  if (footer) {footer.style.display = "none";}
  if (cancelBtn) {
    cancelBtn.style.display = "";
    cancelBtn.onclick = closeForm;
  }
  formHeaderActions
    .querySelector("[data-maintenance-stream-picker-close]")
    ?.addEventListener("click", closeForm);

  formFields.querySelectorAll("[data-maintenance-stream]").forEach((button) => {
    button.addEventListener("click", () => {
      const stream = button.dataset.maintenanceStream || "service";
      closeForm();
      openParentForm("maintenance", getPreparedMaintenanceDefaults({
        ...defaults,
        stream,
      }));
    });
  });

  formModalElement.classList.add("flex");
}

/**
 * Parent-form adapter for Maintenance create/edit fields.
 */
export const maintenanceFormAdapter = {
  getTitle({formItemData, isEditing}) {
    const stream = getMaintenanceStream(formItemData);
    const streamLabel = stream === "issue" ? "Issue" : "Service";
    return `${isEditing ? "Edit" : "Add"} Maintenance ${streamLabel}`;
  },

  getHeaderActionsHtml({formItemData}) {
    const stream = getMaintenanceStream(formItemData);
    const streamMeta = getMaintenanceStreamMeta(stream);

    return `
      <span
        class="maintenance-stream-icon-colored mt-0.5 text-2xl leading-none sm:mt-0 sm:text-[30px]"
        style="--maintenance-stream-outline: ${streamMeta.color}; --maintenance-stream-fill: ${streamMeta.fillColor};"
        title="${attrValue(streamMeta.label)}"
        aria-label="${attrValue(streamMeta.label)}"
      >
        <span class="maintenance-stream-icon-colored__fill material-symbols-outlined" aria-hidden="true">${streamMeta.icon}</span>
        <span class="maintenance-stream-icon-colored__outline material-symbols-outlined">${streamMeta.icon}</span>
      </span>
    `;
  },

  renderFields({appData, formItemData}) {
    const stream = getMaintenanceStream(formItemData);
    const val = (field, fallback = "") => formItemData?.[field] ?? fallback;
    const itemValue = val("item") || val("name");
    const itemLabel = stream === "issue" ? "Issue" : "Item";
    const showServiceCloseFields = stream === "service" && isCloseInterceptStatus({
      appData,
      status: val("status") || getDefaultStreamOptionId(
        appData?.config?.maintenanceStatus || [],
        stream,
      ),
      stream,
    });
    const showIssueCloseFields = stream === "issue" && isCloseInterceptStatus({
      appData,
      status: val("status") || getDefaultStreamOptionId(
        appData?.config?.maintenanceStatus || [],
        stream,
      ),
      stream,
    });

    if (stream === "issue") {
      return `
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input type="hidden" name="stream" value="${attrValue(stream)}">
          ${renderMaintenanceAssetLinkField({
            source: formItemData,
            canOpen: false,
            canLink: true,
            canUnlink: true,
          })}
          ${fieldHtml(itemLabel, inputHtml({
            name: "item",
            value: itemValue,
            required: true,
          }), {required: true, className: "md:col-span-2"})}
          ${renderIssueFields()}
          ${fieldHtml("Type(s)", '<div data-maintenance-type-control></div>')}
          ${fieldHtml("Managed By", '<div data-maintenance-user-control="managed_by"></div>', {required: true})}
          <div id="dynamic-fields-anchor" class="contents"></div>
          ${fieldHtml("Urgency", '<div data-maintenance-scalar-control="priority"></div>')}
          ${fieldHtml("Status", '<div data-maintenance-scalar-control="status"></div>')}
          ${renderIssueDetailsField(val)}
          ${renderIssueCloseFields(val, {showCloseFields: showIssueCloseFields})}
        </div>
      `;
    }

    return `
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <input type="hidden" name="stream" value="${attrValue(stream)}">
        ${renderMaintenanceAssetLinkField({
          source: formItemData,
          canOpen: false,
          canLink: true,
          canUnlink: true,
        })}
        ${fieldHtml(itemLabel, inputHtml({
          name: "item",
          value: itemValue,
          required: true,
        }), {required: true, className: "md:col-span-2"})}
        ${fieldHtml("Type(s)", '<div data-maintenance-type-control></div>')}
        ${fieldHtml("Managed By", '<div data-maintenance-user-control="managed_by"></div>', {required: true})}
        <div id="dynamic-fields-anchor" class="contents"></div>
        ${fieldHtml("Urgency", '<div data-maintenance-scalar-control="priority"></div>')}
        ${fieldHtml("Status", '<div data-maintenance-scalar-control="status"></div>')}
        ${fieldHtml("Technician", `
          ${renderMaintenanceTechnicianControlRoot()}
        `, {required: showServiceCloseFields})}
        ${renderServiceFields(val, {showCloseFields: showServiceCloseFields})}
      </div>
    `;
  },

  mountScalarControls: mountMaintenanceScalarControls,

  async prepareSubmit({data, form, formItemData}) {
    return prepareIssueDetails({data, form, formItemData});
  },

  applySubmitData: applyMaintenanceSubmitData,

  saveItem: saveMaintenanceFormItem,

  afterSave: mergeCreatedMaintenanceIntoCache,
};

registerParentFormAdapter("maintenance", maintenanceFormAdapter);
